import { formatDate, formatTime } from './formatUtils';

/**
 * Génère et lance l'impression d'un examen
 * @param {Object} examen - Les données de l'examen
 * @param {Array} sessions - Les sessions de l'examen
 * @param {Object} etablissement - Les informations de l'établissement
 * @param {number} nombreSessions - Le nombre de sessions (généralement 3)
 */
export const printExam = (examen, sessions, etablissement, nombreSessions = 3) => {
  // Créer une nouvelle fenêtre d'impression dédiée
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Fiche d'examen - ${examen?.libelle || 'Détails'}</title>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 1cm;
            padding: 0;
            color: black;
            background: white;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
            font-size: 12px;
          }
          
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
          }
          
          .logo-container {
            width: 15%;
          }
          
          .center-text {
            width: 70%;
            text-align: center;
          }
          
          .right-info {
            width: 15%;
            text-align: right;
          }
          
          .title-box {
            display: inline-block;
            border: 1px solid #000;
            padding: 5px 20px;
            margin-top: 15px;
          }
          
          .divider {
            width: 100px;
            height: 2px;
            background-color: black;
            margin: 3px auto;
          }
          
          .section-title {
            font-size: 16px;
            font-weight: bold;
            margin: 15px 0;
            border-bottom: 1px solid #333;
            padding-bottom: 5px;
          }
          
          .description-box {
            background-color: #f9f9f9;
            padding: 10px;
            border: 1px solid #ddd;
            margin-bottom: 20px;
          }
          
          .consignes {
            border: 1px dashed #666;
            padding: 10px;
            background-color: #f9f9f9;
            margin: 20px 0;
            font-style: italic;
            font-size: 13px;
          }
          
          .document-date {
            text-align: center;
            margin-top: 30px;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <!-- En-tête institutionnel -->
        <div class="header">
          <div class="header-container">
            <!-- Logo à gauche -->
            ${etablissement?.logo || etablissement?.logo_url ? `
              <div class="logo-container">
                <img src="${etablissement.logo || etablissement.logo_url}" 
                    alt="Logo" style="max-height: 80px; max-width: 100%;">
              </div>
            ` : `<div class="logo-container"></div>`}
            
            <!-- Textes au centre -->
            <div class="center-text">
              <p style="font-weight: bold; text-transform: uppercase; margin: 0 0 2px 0; font-size: 14px;">
                RÉPUBLIQUE ${etablissement?.pays ? `DU ${etablissement.pays.toUpperCase()}` : ''}
              </p>
              <div class="divider"></div>
              ${etablissement?.ministere ? `
                <p style="text-transform: uppercase; margin: 2px 0; font-size: 12px;">
                  MINISTÈRE DE ${etablissement.ministere.toUpperCase()}
                </p>
              ` : ''}
              <h1 style="font-size: 18px; font-weight: bold; margin: 5px 0 2px 0;">
                ${etablissement?.libelle || etablissement?.nom || 'ÉTABLISSEMENT'}
              </h1>
              <p style="font-style: italic; margin: 2px 0; font-size: 14px;">
                ${etablissement?.devise || 'Excellence et Savoir'}
              </p>
              <div style="font-size: 12px; margin: 5px 0;">
                <span>Email: ${etablissement?.email || 'N/A'}</span>
                <span style="margin: 0 5px;">|</span>
                <span>Contact: ${etablissement?.contact || etablissement?.telephone || 'N/A'}</span>
                <span style="margin: 0 5px;">|</span>
                <span>BP: ${etablissement?.bp || 'N/A'}</span>
                ${(etablissement?.site_web || etablissement?.website) ? `
                  <span style="margin: 0 5px;">|</span>
                  <span>Site: ${etablissement.site_web || etablissement.website}</span>
                ` : ''}
              </div>
              <p style="font-size: 12px; margin: 2px 0;">
                ${etablissement?.adresse || 'Adresse non spécifiée'}
              </p>
            </div>
            
            <!-- Année académique à droite -->
            <div class="right-info">
              <div style="border: 1px solid #333; padding: 5px; display: inline-block; text-align: center;">
                <p style="margin: 0 0 2px 0; font-size: 12px;">Année Académique</p>
                <p style="margin: 0; font-weight: bold; font-size: 14px;">
                  ${examen?.annee?.libelle || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          
          <!-- Titre du document -->
          <div style="text-align: center; margin-top: 10px;">
            <h2 class="title-box" style="font-size: 16px; font-weight: bold; letter-spacing: 1px; margin: 0;">
              FICHE D'EXAMEN
            </h2>
            <h3 style="font-size: 16px; margin: 10px 0;">
              ${examen?.libelle || 'Examen'}
            </h3>
          </div>
        </div>
        
        <!-- Informations générales -->
        <table>
          <tr>
            <th>Classe</th>
            <th>Période</th>
            <th>Statut</th>
            <th>Sessions</th>
          </tr>
          <tr>
            <td>${examen?.classe?.libelle || 'Non spécifiée'}</td>
            <td>${formatDate(examen?.date_debut) || ''} - ${formatDate(examen?.date_fin) || ''}</td>
            <td>${examen?.status || 'PROGRAMME'}</td>
            <td>${nombreSessions || 3}</td>
          </tr>
        </table>
        
        <!-- Description si disponible -->
        ${examen?.description ? `
          <div class="description-box">
            <h3 style="margin: 0 0 5px 0; font-size: 14px;">Description</h3>
            <p style="margin: 0; font-size: 13px;">${examen.description}</p>
          </div>
        ` : ''}
        
        <!-- Liste des sessions -->
        <h3 class="section-title">PROGRAMME DES SESSIONS</h3>
        <table>
          <thead>
            <tr>
              <th style="width: 100px; text-align: center;">Date</th>
              <th>Module</th>
              <th style="width: 100px; text-align: center;">Horaire</th>
              <th style="width: 100px; text-align: center;">Salle</th>
              <th style="text-align: center;">Surveillant</th>
            </tr>
          </thead>
          <tbody>
            ${Array.isArray(sessions) && sessions.length > 0 ? 
              sessions.map((session) => `
                <tr>
                  <td style="text-align: center;">${formatDate(session.date_examen) || ''}</td>
                  <td>${session.module?.libelle || (session.module?.module?.libelle) || 'Non défini'}</td>
                  <td style="text-align: center;">${formatTime(session.heure_debut) || ''} - ${formatTime(session.heure_fin) || ''}</td>
                  <td style="text-align: center;">${session.salle?.libelle || 'Non définie'}</td>
                  <td style="text-align: center;">
                    ${session.surveillant_principal ? 
                      `${session.surveillant_principal.first_name || ''} ${session.surveillant_principal.last_name || ''}` : 
                      'Non assigné'
                    }
                  </td>
                </tr>
              `).join('') : 
              '<tr><td colspan="5" style="text-align: center; padding: 15px;">Aucune session programmée</td></tr>'
            }
          </tbody>
        </table>
        
        <!-- Consignes/instructions -->
        <div class="consignes">
          <p style="margin: 0;">
            Les étudiants doivent se présenter 15 minutes avant le début de chaque épreuve, munis de leur carte d'étudiant.
            <br>Tout retard de plus de 30 minutes entrainera le refus d'accès à la salle d'examen.
          </p>
        </div>
        
        <!-- Date du document -->
        <div class="document-date">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')}</p>
        </div>
      </body>
    </html>
  `);
  
  // Fermer le document et imprimer
  printWindow.document.close();
  
  // Un délai plus long pour s'assurer que tout est chargé avant l'impression
  setTimeout(() => {
    try {
      printWindow.focus();
      printWindow.print();
      console.log("Impression lancée avec succès");
    } catch (error) {
      console.error("Erreur lors de l'impression:", error);
      alert("Erreur lors de l'impression. Veuillez réessayer.");
    }
  }, 1500);
}; 